import { computed, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import { UserStore } from '@platform/store';
import { cyrb53, stableJsonStringify } from '@platform/shared/helpers/role-helpers';
import { snakeCase } from 'lodash-es';
import { checkRoleCapability, roleRequestState } from '@platform/shared/lib/roles';
import { checkIsInMainView, findAppropriateCompProps } from '@platform/shared/lib/roles/internal';

export function useRoleScope(comp?: string, compProps?: any) {
    const instance = getCurrentInstance();
    const route = useRoute();
    const { user } = UserStore.useLoginState();
    const getRoleScopeString = () => {
        const repr = findAppropriateCompProps(
            comp ?? instance?.type.name ?? null,
            compProps ?? route.matched[route.matched.length - 1].meta?.rawProps ?? null,
            user.value?.role?.capabilities
        );
        if (!repr.c) return null;
        return cyrb53(`component:${snakeCase(repr.c)}^${stableJsonStringify(repr.cp)}`);
    };
    const getRoleScopeStringDescription = () => {
        const repr = findAppropriateCompProps(
            comp ?? instance?.type.name ?? null,
            compProps ?? route.matched[route.matched.length - 1].meta?.rawProps ?? null,
            user.value?.role?.capabilities
        );
        if (!repr.c) return null;
        return `component:${snakeCase(repr.c)}^${stableJsonStringify(repr.cp)}`;
    };
    const currentRoleCapability = computed(() => {
        const repr = findAppropriateCompProps(
            comp ?? instance?.type.name ?? null,
            compProps ?? route.matched[route.matched.length - 1].meta?.rawProps ?? null,
            user.value?.role?.capabilities
        );
        if (!repr.c) return null;
        return (
            user.value?.role.capabilities.get(`component:${snakeCase(repr.c)}^${stableJsonStringify(repr.cp)}`) ?? null
        );
    });
    const getRoleFnCapability = (fn: string) => {
        return user.value?.role.capabilities.get(`fn:${fn}`) || null;
    };

    const isInMainView = checkIsInMainView(instance?.parent || null);

    return {
        getRoleScopeString,
        role: computed(() => user.value?.role),
        currentRoleCapability,
        getRoleFnCapability,
        prepareRoleForRequest: (force?: boolean) => {
            if (force || (isInMainView && !roleRequestState.ignoreRole))
                roleRequestState.current = getRoleScopeString()?.toString() ?? null;
            roleRequestState.description = getRoleScopeStringDescription();
        },
        checkSpecificCapability: checkRoleCapability,
    };
}
